// Teacher Detail Page - SAT V4 Zone
import { DsButton, DsCard, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import type { FC, SyntheticEvent } from 'react';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUserManagementStore } from '../../../../stores';
import { routes } from '../../../../routes/routes';
import { teacherDetailTabConfigs } from '../../MockData';
import ProfileTab from './ProfileTab';
import SubjectsTab from './SubjectsTab';
import WorkloadTab from './WorkloadTab';
import AvailabilityTab from './AvailabilityTab';
import RulesPreferencesTab from './RulesPreferencesTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`teacher-detail-tabpanel-${index}`}
    aria-labelledby={`teacher-detail-tab-${index}`}
    sx={{ pt: 3 }}
  >
    {value === index && children}
  </Box>
);

const TeacherDetail: FC = () => {
  const { teacherId } = useParams<{ teacherId: string }>();
  const navigate = useNavigate();
  const { getTeacherById, updateTeacher } = useUserManagementStore();
  const [activeTab, setActiveTab] = useState(0);

  const teacher = teacherId ? getTeacherById(teacherId) : undefined;

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(routes.userManagement);
  };

  const handleUpdate = (updates: Partial<typeof teacher>) => {
    if (teacherId) {
      updateTeacher(teacherId, updates);
    }
  };

  if (!teacher) {
    return (
      <Box sx={{ p: 3 }}>
        <DsCard sx={{ p: 4, textAlign: 'center' }}>
          <DsIcon icon="ALERT_WARNING" sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <DsTypography variant="h6" gutterBottom>
            Teacher Not Found
          </DsTypography>
          <DsTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The teacher with ID "{teacherId}" could not be found.
          </DsTypography>
          <DsButton variant="contained" onClick={handleBack}>
            Back to Teachers
          </DsButton>
        </DsCard>
      </Box>
    );
  }

  const tabComponents = [
    <ProfileTab key="profile" teacher={teacher} onUpdate={handleUpdate} />,
    <SubjectsTab key="subjects" teacher={teacher} onUpdate={handleUpdate} />,
    <WorkloadTab key="workload" teacher={teacher} />,
    <AvailabilityTab key="availability" teacher={teacher} onUpdate={handleUpdate} />,
    <RulesPreferencesTab key="rulesPreferences" teacher={teacher} onUpdate={handleUpdate} />,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={2}>
          <DsButton
            variant="text"
            startIcon={<DsIcon icon="ARROW_LEFT" />}
            onClick={handleBack}
          >
            Back
          </DsButton>
          <Box>
            <DsTypography variant="h4" fontWeight={700}>
              {teacher.title} {teacher.firstName} {teacher.lastName}
            </DsTypography>
            <DsTypography variant="body2" color="text.secondary">
              {teacher.employeeId} • {teacher.email}
            </DsTypography>
          </Box>
        </Stack>

        {/* Tabs Card */}
        <DsCard sx={{ p: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="Teacher detail tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {teacherDetailTabConfigs.map((config, index) => (
                <Tab
                  key={config.id}
                  label={config.label}
                  id={`teacher-detail-tab-${index}`}
                  aria-controls={`teacher-detail-tabpanel-${index}`}
                  disabled={index === 2 || index === 3 || index === 4}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {tabComponents.map((component, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                {component}
              </TabPanel>
            ))}
          </Box>
        </DsCard>
      </Stack>
    </Box>
  );
};

export default TeacherDetail;

